<template>
  <div class="sidebar__section sidebar__section--scrollable">
    <div class="sidebar__section-scrollable">
      <div
        class="sidebar__section-scrollable-inner"
        data-highlight="applications"
      >
        <ul v-if="pendingJobs[null].length" class="tree">
          <component
            :is="getPendingJobComponent(job)"
            v-for="job in pendingJobs[null]"
            :key="job.id"
            :job="job"
          >
          </component>
        </ul>

        <!-- Origem Flow: as seções são renderizadas na ordem escolhida pelo
             usuário (arrastar pelo título da seção); persistida por
             workspace no localStorage. -->
        <template v-for="sectionKey in sectionOrder">
          <!-- Chats com IA (estilo ClickUp) -->
          <ul
            v-if="sectionKey === 'ai-chats'"
            :key="sectionKey + '-ai-chats'"
            v-sortable="sectionSortable('ai-chats')"
            class="tree"
          >
            <li>
              <div
                class="tree__heading tree__heading--collapsible"
                @click="toggleSectionCollapsed('ai-chats')"
              >
                <i
                  class="tree__heading-caret"
                  :class="
                    isSectionCollapsed('ai-chats')
                      ? 'iconoir-nav-arrow-right'
                      : 'iconoir-nav-arrow-down'
                  "
                ></i>
                <div class="tree__heading-name">
                  {{ $t('sidebar.aiChats') }}
                </div>
              </div>
              <ul v-show="!isSectionCollapsed('ai-chats')" class="tree">
                <nuxt-link
                  v-slot="{ href, navigate, isExactActive }"
                  custom
                  :to="{
                    name: 'workspace-ai',
                    params: { workspaceId: selectedWorkspace.id },
                  }"
                >
                  <li class="tree__item" :class="{ active: isExactActive }">
                    <div class="tree__action sidebar__action">
                      <a :href="href" class="tree__link" @click="navigate">
                        <i class="tree__icon iconoir-plus"></i>
                        <span class="tree__link-text">
                          <span class="sidebar__item-name">{{
                            $t('sidebar.askBuildCreate')
                          }}</span>
                        </span>
                      </a>
                    </div>
                  </li>
                </nuxt-link>
              </ul>
            </li>
          </ul>

          <!-- Espaços (estilo ClickUp): todas as aplicações numa única seção -->
          <ul
            v-else-if="sectionKey === 'spaces'"
            :key="sectionKey + '-spaces'"
            v-sortable="sectionSortable('spaces')"
            class="tree"
          >
            <!-- Origem Flow: botão direito em qualquer ponto livre da seção
               (cabeçalho, área vazia) abre o menu do espaço. Os itens que já
               têm menu próprio param a propagação. -->
            <li @contextmenu.prevent="openSpacesContext($event)">
              <div
                class="tree__heading tree__heading--collapsible"
                @click="toggleSectionCollapsed('spaces')"
              >
                <i
                  class="tree__heading-caret"
                  :class="
                    isSectionCollapsed('spaces')
                      ? 'iconoir-nav-arrow-right'
                      : 'iconoir-nav-arrow-down'
                  "
                ></i>
                <div class="tree__heading-name">
                  {{ $t('sidebar.spaces') }}
                </div>
                <a
                  v-if="canCreateApplication"
                  ref="createApplicationHeadingLink"
                  class="tree__heading-add"
                  @click.stop="
                    openCreateApplication('createApplicationHeadingLink')
                  "
                >
                  <i class="iconoir-plus"></i>
                </a>
              </div>
              <template v-if="!isSectionCollapsed('spaces')">
                <ul class="tree">
                  <nuxt-link
                    v-slot="{ href, navigate, isExactActive }"
                    custom
                    :to="{
                      name: 'workspace-tasks',
                      params: { workspaceId: selectedWorkspace.id },
                    }"
                  >
                    <li
                      class="tree__item"
                      :class="{ active: isExactActive || allTasksTableOpen }"
                    >
                      <div class="tree__action sidebar__action">
                        <a :href="href" class="tree__link" @click="navigate">
                          <i class="tree__icon iconoir-view-grid"></i>
                          <span class="tree__link-text">
                            <span class="sidebar__item-name">{{
                              $t('sidebar.everything')
                            }}</span>
                          </span>
                        </a>
                      </div>
                    </li>
                  </nuxt-link>
                </ul>
                <!-- Origem Flow: os espaços formam uma lista única, na ordem
                   escolhida pelo usuário. Antes eram agrupados por tipo de
                   aplicação, então arrastar ou ordenar por nome só valia
                   dentro do próprio tipo. -->
                <ul
                  v-if="applicationsForSelectedWorkspace.length"
                  class="tree margin-bottom-0"
                  data-highlight="applications-database"
                >
                  <component
                    :is="getApplicationComponent(application)"
                    v-for="application in applicationsForSelectedWorkspace"
                    :key="application.id"
                    v-sortable="{
                      id: application.id,
                      update: orderApplications,
                      handle: '[data-sortable-handle]',
                      marginTop: -1.5,
                      enabled: $hasPermission(
                        'workspace.order_applications',
                        selectedWorkspace,
                        selectedWorkspace.id
                      ),
                    }"
                    :application="application"
                    :workspace="selectedWorkspace"
                  ></component>
                </ul>
                <template
                  v-for="applicationGroup in groupedApplicationsForSelectedWorkspace"
                >
                  <ul
                    v-if="pendingJobs[applicationGroup.type].length"
                    :key="applicationGroup.type + '-jobs'"
                    class="tree margin-bottom-0"
                  >
                    <component
                      :is="getPendingJobComponent(job)"
                      v-for="job in pendingJobs[applicationGroup.type]"
                      :key="job.id"
                      :job="job"
                    >
                    </component>
                  </ul>
                </template>
                <!-- Docs do workspace na árvore do espaço (estilo ClickUp),
                   numa pasta recolhível para não tomar o espaço da árvore.
                   Botão direito abre o menu de criar/ordenar documentos. -->
                <ul class="tree margin-bottom-0">
                  <li class="tree__item">
                    <div class="tree__action sidebar__action">
                      <a
                        class="tree__link"
                        @click="toggleSectionCollapsed('documents')"
                        @contextmenu.prevent.stop="openDocumentsContext($event)"
                      >
                        <i
                          class="tree__icon"
                          :class="
                            isSectionCollapsed('documents')
                              ? 'iconoir-folder'
                              : 'iconoir-remove-folder'
                          "
                        ></i>
                        <span class="tree__link-text">
                          <span class="sidebar__item-name">{{
                            $t('sidebar.documents')
                          }}</span>
                        </span>
                        <span class="sidebar__item-count">{{
                          sidebarDocuments.length
                        }}</span>
                      </a>
                    </div>
                  </li>
                  <template v-if="!isSectionCollapsed('documents')">
                    <li
                      v-for="doc in sortedSidebarDocuments"
                      :key="'doc-' + doc.id"
                      v-sortable="{
                        id: doc.id,
                        update: orderDocuments,
                        marginTop: -1.5,
                      }"
                      class="tree__item"
                      :class="{
                        active:
                          $route.name === 'workspace-docs' &&
                          parseInt($route.query.doc) === doc.id,
                      }"
                    >
                      <div
                        class="tree__action sidebar__action sidebar__action--nested"
                      >
                        <a
                          class="tree__link"
                          @click="openDocument(doc)"
                          @contextmenu.prevent.stop="
                            openDocumentsContext($event)
                          "
                        >
                          <i
                            class="tree__icon sidebar__doc-icon"
                            :class="docIcon(doc)"
                          ></i>
                          <span class="tree__link-text">
                            <span class="sidebar__item-name">{{
                              doc.name || $t('docsHub.untitled')
                            }}</span>
                          </span>
                        </a>
                      </div>
                    </li>
                  </template>
                </ul>
                <ul v-if="canCreateApplication" class="tree">
                  <li class="tree__item">
                    <div class="tree__action sidebar__action">
                      <a
                        ref="createApplicationContextLink"
                        class="tree__link"
                        @click="
                          openCreateApplication('createApplicationContextLink')
                        "
                      >
                        <i class="tree__icon iconoir-plus"></i>
                        <span class="tree__link-text">
                          <span class="sidebar__item-name">{{
                            $t('sidebar.newSpace')
                          }}</span>
                        </span>
                      </a>
                    </div>
                  </li>
                </ul>
              </template>
            </li>
          </ul>

          <!-- Canais (estilo ClickUp) -->
          <ul
            v-else-if="sectionKey === 'channels'"
            :key="sectionKey + '-channels'"
            v-sortable="sectionSortable('channels')"
            class="tree"
          >
            <li>
              <div
                class="tree__heading tree__heading--collapsible"
                @click="toggleSectionCollapsed('channels')"
              >
                <i
                  class="tree__heading-caret"
                  :class="
                    isSectionCollapsed('channels')
                      ? 'iconoir-nav-arrow-right'
                      : 'iconoir-nav-arrow-down'
                  "
                ></i>
                <div class="tree__heading-name">
                  {{ $t('sidebar.channels') }}
                </div>
              </div>
              <ul v-show="!isSectionCollapsed('channels')" class="tree">
                <li class="tree__item">
                  <div class="sidebar__empty-section">
                    {{ $t('sidebar.channelsEmpty') }}
                  </div>
                </li>
              </ul>
            </li>
          </ul>

          <!-- Mensagens diretas (estilo ClickUp) -->
          <ul
            v-else-if="sectionKey === 'dms'"
            :key="sectionKey + '-dms'"
            v-sortable="sectionSortable('dms')"
            class="tree"
          >
            <li>
              <div
                class="tree__heading tree__heading--collapsible"
                @click="toggleSectionCollapsed('dms')"
              >
                <i
                  class="tree__heading-caret"
                  :class="
                    isSectionCollapsed('dms')
                      ? 'iconoir-nav-arrow-right'
                      : 'iconoir-nav-arrow-down'
                  "
                ></i>
                <div class="tree__heading-name">
                  {{ $t('sidebar.directMessages') }}
                </div>
              </div>
              <ul v-show="!isSectionCollapsed('dms')" class="tree">
                <li class="tree__item">
                  <div class="sidebar__empty-section">
                    {{ $t('sidebar.directMessagesEmpty') }}
                  </div>
                </li>
              </ul>
            </li>
          </ul>
        </template>
      </div>
    </div>
    <CreateApplicationContext
      ref="createApplicationContext"
      :workspace="selectedWorkspace"
    ></CreateApplicationContext>
    <!-- Origem Flow: menu do botão direito da seção "Espaços". -->
    <Context ref="spacesContext" overflow-scroll max-height-if-outside-viewport>
      <ul class="context__menu">
        <li v-if="canCreateApplication" class="context__menu-item">
          <a class="context__menu-item-link" @click="openCreateApplication()">
            <i class="context__menu-item-icon iconoir-plus"></i>
            {{ $t('sidebar.newSpace') }}
          </a>
        </li>
        <li class="context__menu-item">
          <a
            class="context__menu-item-link"
            @click="toggleSpacesCollapsedFromContext()"
          >
            <i
              class="context__menu-item-icon"
              :class="
                isSectionCollapsed('spaces')
                  ? 'iconoir-nav-arrow-down'
                  : 'iconoir-nav-arrow-right'
              "
            ></i>
            {{
              isSectionCollapsed('spaces')
                ? $t('spacesContext.expand')
                : $t('spacesContext.collapse')
            }}
          </a>
        </li>
        <li
          v-if="canOrderApplications"
          class="context__menu-item context__menu-item--with-separator"
        >
          <div class="context__menu-title">
            {{ $t('spacesContext.sortTitle') }}
          </div>
        </li>
        <template v-if="canOrderApplications">
          <li class="context__menu-item">
            <a class="context__menu-item-link" @click="sortSpaces('asc')">
              <i class="context__menu-item-icon iconoir-sort-down"></i>
              {{ $t('spacesContext.sortAsc') }}
            </a>
          </li>
          <li class="context__menu-item">
            <a class="context__menu-item-link" @click="sortSpaces('desc')">
              <i class="context__menu-item-icon iconoir-sort-up"></i>
              {{ $t('spacesContext.sortDesc') }}
            </a>
          </li>
        </template>
      </ul>
    </Context>
    <!-- Origem Flow: menu do botão direito da pasta "Documentos". -->
    <Context
      ref="documentsContext"
      overflow-scroll
      max-height-if-outside-viewport
    >
      <ul class="context__menu context__menu--can-be-active">
        <li class="context__menu-item">
          <a class="context__menu-item-link" @click="openCreateDocumentModal()">
            <i class="context__menu-item-icon iconoir-plus"></i>
            {{ $t('docsHub.newDocument') }}
          </a>
        </li>
        <li class="context__menu-item context__menu-item--with-separator">
          <div class="context__menu-title">
            {{ $t('docsSort.title') }}
          </div>
        </li>
        <li
          v-for="option in docsSortOptions"
          :key="option"
          class="context__menu-item"
        >
          <a
            class="context__menu-item-link"
            :class="{ active: docsSortBy === option }"
            @click="setDocsSort(option)"
          >
            <i
              class="context__menu-item-icon"
              :class="docsSortIcons[option]"
            ></i>
            {{ $t('docsSort.' + option) }}
          </a>
        </li>
      </ul>
    </Context>
    <CreateDocumentModal
      ref="createDocumentModal"
      :workspace-id="selectedWorkspace.id"
      @created="documentCreated($event)"
    ></CreateDocumentModal>
  </div>
</template>

<script>
import { notifyIf } from '@baserow/modules/core/utils/error'
import CreateApplicationContext from '@baserow/modules/core/components/application/CreateApplicationContext'
import DocsService from '@baserow/modules/core/services/docs'
import CreateDocumentModal from '@baserow/modules/core/components/docs/CreateDocumentModal'
import {
  readCollapsedSections,
  writeCollapsedSections,
} from '@baserow/modules/core/utils/sidebarCollapse'
import {
  readSidebarOrder,
  writeSidebarOrder,
} from '@baserow/modules/core/utils/sidebarOrder'
import {
  DOCS_SORT_OPTIONS,
  docTypeIcon,
  readDocsSort,
  sortDocuments,
  writeDocsSort,
} from '@baserow/modules/core/utils/docsSort'

/**
 * Origem Flow: sidebar de conteúdo no formato do ClickUp — seções "Chats com
 * IA", "Espaços" (com "Todas as tarefas", as aplicações e "+ Novo Espaço"),
 * "Canais" e "Mensagens diretas", todas colapsáveis.
 */
export default {
  name: 'SidebarWithWorkspace',
  components: { CreateApplicationContext, CreateDocumentModal },
  props: {
    applications: {
      type: Array,
      required: true,
    },
    selectedWorkspace: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      // Seções colapsadas (estilo "Espaços" do ClickUp); persistido no
      // localStorage. Canais e Mensagens diretas começam colapsadas.
      collapsedSections: { channels: true, dms: true },
      // Docs do workspace exibidos na árvore do espaço (estilo ClickUp).
      sidebarDocuments: [],
      // Ordenação dos documentos escolhida no menu do botão direito.
      docsSortBy: 'recent',
      docsSortOptions: DOCS_SORT_OPTIONS,
      docsSortIcons: {
        recent: 'iconoir-clock',
        alphabetical: 'iconoir-sort-down',
        type: 'iconoir-page',
        manual: 'iconoir-drag-hand-gesture',
      },
      // Ordem das seções da sidebar (arrastar pelo título para trocar).
      sectionOrder: ['ai-chats', 'spaces', 'channels', 'dms'],
    }
  },
  computed: {
    /**
     * Origem Flow: "Todas as tarefas" fica ativo quando a tabela aberta é o
     * espelho agregado do espaço (a página redireciona para ela).
     */
    allTasksTableOpen() {
      const table = this.$store.getters['table/getSelected']
      return !!(table && table.all_tasks)
    },
    /**
     * Because all the applications that belong to the user are in the store we will
     * filter on the selected workspace here.
     */
    groupedApplicationsForSelectedWorkspace() {
      const applicationTypes = this.$registry
        .getOrderedList('application')
        .map((applicationType) => {
          return {
            name: applicationType.getNamePlural(),
            type: applicationType.getType(),
            applicationType,
            developmentStage: applicationType.developmentStage,
            applications: this.applications
              .filter((application) => {
                return (
                  application.workspace.id === this.selectedWorkspace.id &&
                  application.type === applicationType.getType() &&
                  applicationType.isVisible(application)
                )
              })
              .sort((a, b) => a.order - b.order),
          }
        })
      return applicationTypes
    },
    /**
     * Origem Flow: todos os espaços visíveis do workspace numa lista só, na
     * ordem gravada — é o que o arrastar e o "Ordenar espaços" manipulam.
     */
    applicationsForSelectedWorkspace() {
      return this.applications
        .filter((application) => {
          const applicationType = this.$registry.get(
            'application',
            application.type
          )
          return (
            application.workspace.id === this.selectedWorkspace.id &&
            applicationType.isVisible(application)
          )
        })
        .sort((a, b) => a.order - b.order)
    },
    pendingJobs() {
      const grouped = { null: [] }
      Object.values(this.$registry.getAll('application')).forEach(
        (applicationType) => {
          grouped[applicationType.getType()] = []
        }
      )
      this.$store.getters['job/getAll'].forEach((job) => {
        const jobType = this.$registry.get('job', job.type)
        if (jobType.isJobPartOfWorkspace(job, this.selectedWorkspace)) {
          grouped[jobType.getSidebarApplicationTypeLocation(job)].push(job)
        }
      })
      return grouped
    },
    canCreateApplication() {
      return this.$hasPermission(
        'workspace.create_application',
        this.selectedWorkspace,
        this.selectedWorkspace.id
      )
    },
    canOrderApplications() {
      return this.$hasPermission(
        'workspace.order_applications',
        this.selectedWorkspace,
        this.selectedWorkspace.id
      )
    },
    sortedSidebarDocuments() {
      return sortDocuments(this.sidebarDocuments, this.docsSortBy, {
        blankLabel: this.$t('docsHub.blankDocument'),
      })
    },
  },
  watch: {
    'selectedWorkspace.id': {
      handler() {
        this.fetchSidebarDocuments()
      },
    },
  },
  mounted() {
    // Origem Flow: a pasta "Documentos" SEMPRE nasce recolhida (pedido da
    // tabela de ajustes, linha 17) — o estado aberto vale só para a sessão
    // atual e não sobrevive ao reload, por isso vem depois do spread.
    this.collapsedSections = {
      channels: true,
      dms: true,
      ...readCollapsedSections(),
      documents: true,
    }
    this.docsSortBy = readDocsSort()
    this.sectionOrder = readSidebarOrder('sections', this.sectionOrder)
    this.fetchSidebarDocuments()
  },
  methods: {
    async fetchSidebarDocuments() {
      const workspaceId = this.selectedWorkspace?.id
      if (!workspaceId) {
        this.sidebarDocuments = []
        return
      }
      try {
        const { data } = await DocsService(this.$client).fetchAll(workspaceId)
        this.sidebarDocuments = data.documents.filter((d) => !d.parent_id)
      } catch (error) {
        // Sidebar continua funcional sem os docs.
        this.sidebarDocuments = []
      }
    },
    openDocument(doc) {
      this.$router.push({
        name: 'workspace-docs',
        params: { workspaceId: this.selectedWorkspace.id },
        query: { doc: doc.id },
      })
    },
    docIcon(doc) {
      return docTypeIcon(doc.doc_type)
    },
    /**
     * Origem Flow: menu do botão direito da seção "Espaços" — abre junto ao
     * cursor, como o menu nativo que ele substitui.
     */
    openSpacesContext(event) {
      this.$refs.spacesContext.showNextToMouse(event, 'bottom', 'right', 0)
    },
    /**
     * As seções são renderizadas dentro de um `v-for`, então o Vue 3 entrega
     * cada `ref` como array. Sem desembrulhar, o Context recebia um array em
     * vez do elemento e abria fora do lugar.
     */
    resolveElementRef(name) {
      const element = this.$refs[name]
      return Array.isArray(element) ? element[0] : element
    },
    openCreateApplication(refName = 'createApplicationHeadingLink') {
      const fromContext = this.$refs.spacesContext.open
      this.$refs.spacesContext.hide()
      const target =
        this.resolveElementRef(refName) ||
        this.resolveElementRef('createApplicationHeadingLink')
      if (!fromContext) {
        this.$refs.createApplicationContext.toggle(target)
        return
      }
      // Aberto de dentro do menu do botão direito: o clique atual ainda está
      // subindo até o body, e o "clicar fora" que o novo menu registra o
      // fecharia no mesmo instante. Abrir depois da propagação resolve.
      window.setTimeout(() => {
        this.$refs.createApplicationContext.show(target, 'bottom', 'left')
      }, 0)
    },
    toggleSpacesCollapsedFromContext() {
      this.$refs.spacesContext.hide()
      this.toggleSectionCollapsed('spaces')
    },
    /**
     * Origem Flow: ordena os espaços por nome e grava a nova ordem, para que
     * ela valha também nos outros dispositivos.
     */
    async sortSpaces(direction) {
      this.$refs.spacesContext.hide()
      const applications = this.applicationsForSelectedWorkspace
      const oldOrder = applications.map((application) => application.id)
      const order = [...applications]
        .sort((a, b) =>
          direction === 'asc'
            ? a.name.localeCompare(b.name)
            : b.name.localeCompare(a.name)
        )
        .map((application) => application.id)
      if (order.every((id, index) => id === oldOrder[index])) {
        return
      }
      try {
        await this.$store.dispatch('application/order', {
          workspace: this.selectedWorkspace,
          order,
          oldOrder,
        })
      } catch (error) {
        notifyIf(error, 'application')
      }
    },
    openDocumentsContext(event) {
      this.$refs.documentsContext.toggle(
        event.currentTarget,
        'bottom',
        'left',
        4
      )
    },
    openCreateDocumentModal() {
      this.$refs.documentsContext.hide()
      this.$refs.createDocumentModal.show()
    },
    documentCreated({ type, document: doc }) {
      if (type === 'document' && doc && !doc.parent_id) {
        this.sidebarDocuments = [doc, ...this.sidebarDocuments]
      }
    },
    setDocsSort(option) {
      this.docsSortBy = option
      writeDocsSort(option)
      this.$refs.documentsContext.hide()
      // Avisa o hub de Docs aberto (se houver) para reordenar a lista.
      window.dispatchEvent(
        new CustomEvent('origem-flow-docs-sort', { detail: option })
      )
    },
    isSectionCollapsed(key) {
      return !!this.collapsedSections[key]
    },
    toggleSectionCollapsed(key) {
      // Grava false explícito (em vez de remover a chave) para que abrir
      // uma seção recolhida por padrão sobreviva ao reload.
      const sections = {
        ...this.collapsedSections,
        [key]: !this.collapsedSections[key],
      }
      this.collapsedSections = sections
      writeCollapsedSections(sections)
    },
    getApplicationComponent(application) {
      return this.$registry
        .get('application', application.type)
        .getSidebarComponent()
    },
    getPendingJobComponent(job) {
      return this.$registry.get('job', job.type).getSidebarComponent()
    },
    async orderApplications(order, oldOrder) {
      try {
        await this.$store.dispatch('application/order', {
          workspace: this.selectedWorkspace,
          order,
          oldOrder,
        })
      } catch (error) {
        notifyIf(error, 'application')
      }
    },
    /**
     * Origem Flow: configuração do v-sortable das seções — arrasta pelo
     * título da seção; o corpo continua livre para os itens internos.
     */
    sectionSortable(key) {
      return {
        id: key,
        update: this.orderSections,
        handle: '.tree__heading',
      }
    },
    orderSections(newOrder) {
      const valid = newOrder.filter((key) =>
        ['ai-chats', 'spaces', 'channels', 'dms'].includes(key)
      )
      if (valid.length !== 4) {
        return
      }
      this.sectionOrder = valid
      writeSidebarOrder('sections', valid)
    },
    /**
     * Origem Flow: reordenação manual dos documentos por arrasto — grava a
     * nova ordem no backend e muda o modo de ordenação para "manual".
     */
    async orderDocuments(newOrder) {
      const ids = newOrder.filter((id) => typeof id === 'number')
      if (ids.length === 0) {
        return
      }
      this.docsSortBy = 'manual'
      writeDocsSort('manual')
      const byId = Object.fromEntries(
        this.sidebarDocuments.map((doc) => [doc.id, doc])
      )
      const updates = []
      ids.forEach((id, index) => {
        const doc = byId[id]
        if (doc && doc.order !== index + 1) {
          doc.order = index + 1
          updates.push(
            DocsService(this.$client).update(id, { order: index + 1 })
          )
        }
      })
      // Reatribui para o computed sortedSidebarDocuments recalcular.
      this.sidebarDocuments = [...this.sidebarDocuments]
      window.dispatchEvent(
        new CustomEvent('origem-flow-docs-sort', { detail: 'manual' })
      )
      try {
        await Promise.all(updates)
      } catch (error) {
        notifyIf(error, 'application')
      }
    },
  },
}
</script>
